import { ScrollTrigger, gsap } from "@client/gsap";
import colors from "@client/styles/color-variables.module.scss";
import themes from "@client/styles/theming.module.css";
import { useGSAP } from "@gsap/react";
import React from "react";

import { ServicesStoryBlock } from "@reactivated";

import { MediaChooserBlock } from "@thelabnyc/thelabui/src/components/MediaChooserBlock";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { RichTextBlock } from "../RichText";
import { ScrollIndicator } from "../ScrollIndicator";

import styles from "./services.module.scss";

gsap.registerPlugin(ScrollTrigger);

const a = (5 * Math.PI) / 6;
const cos = Math.cos(a);
const sin = Math.sin(a);

const CIRCLE_POSITIONS = {
    blue: { cx: 0, cy: -50 },
    teal: { cx: -cos * 50, cy: sin * 50 },
    red: { cx: cos * 50, cy: sin * 50 },
} as const;

export default function StoryBlock({
    value: props,
}: {
    value: ServicesStoryBlock;
}) {
    const sliderRef = React.useRef<HTMLDivElement>(null);
    useGSAP(() => {
        const slider = sliderRef.current;
        if (!slider) return;
        gsap.to(slider, {
            scrollTrigger: {
                trigger: slider,
                scrub: 0.3,
            },
            x: "-100%",
            ease: "none",
        });
    }, []);

    const blueRef = React.useRef<SVGCircleElement>(null);
    const tealRef = React.useRef<SVGCircleElement>(null);
    const redRef = React.useRef<SVGCircleElement>(null);
    const heroRef = React.useRef<HTMLDivElement>(null);
    const insightsRef = React.useRef<HTMLDivElement>(null);
    const creativeRef = React.useRef<HTMLDivElement>(null);
    const technologyRef = React.useRef<HTMLDivElement>(null);
    const bodyRef = React.useRef<HTMLDivElement>(null);
    const graphicRef = React.useRef<HTMLDivElement>(null);

    useGSAP(() => {
        const blue = blueRef.current;
        const teal = tealRef.current;
        const red = redRef.current;
        const container = graphicRef.current;
        const hero = heroRef.current;
        const insights = insightsRef.current;
        const creative = creativeRef.current;
        const technology = technologyRef.current;
        const body = bodyRef.current;
        if (
            !(
                blue &&
                teal &&
                red &&
                container &&
                hero &&
                insights &&
                creative &&
                technology &&
                body
            )
        ) {
            return;
        }

        let tl: gsap.core.Timeline | null = null;
        let st: ScrollTrigger | null = null;

        const build = () => {
            tl?.kill();
            st?.kill();

            // Restore initial visual state so the new timeline captures
            // fresh FROM values on its first render. Without this, a rebuild
            // after the user has scrolled through r1/r2/etc. would inherit
            // already-faded alphas as the new FROM, leaving circles stuck.
            gsap.set(blue, {
                rotation: 0,
                scale: 1,
                alpha: 0.7,
                cx: CIRCLE_POSITIONS.blue.cx,
                cy: CIRCLE_POSITIONS.blue.cy,
            });
            gsap.set(teal, {
                rotation: 0,
                scale: 1,
                alpha: 0.7,
                cx: CIRCLE_POSITIONS.teal.cx,
                cy: CIRCLE_POSITIONS.teal.cy,
            });
            gsap.set(red, {
                rotation: 0,
                scale: 1,
                alpha: 0.7,
                cx: CIRCLE_POSITIONS.red.cx,
                cy: CIRCLE_POSITIONS.red.cy,
            });
            gsap.set(container, { alpha: 1 });

            const documentY = (el: HTMLElement) =>
                el.getBoundingClientRect().top + window.scrollY;
            const viewportH = window.innerHeight;

            const startY = documentY(hero);
            const endY =
                documentY(body) + body.offsetHeight / 2 - viewportH / 2;
            const range = endY - startY;
            if (range <= 0) return;

            const clamp = (n: number) => Math.max(0, Math.min(1, n));
            const headingCenter = (el: HTMLElement) => {
                const h3: HTMLElement = el.querySelector("h3") ?? el;
                return clamp(
                    (documentY(h3) +
                        h3.offsetHeight / 2 -
                        viewportH / 2 -
                        startY) /
                        range,
                );
            };
            const headingTop = (el: HTMLElement) => {
                const h3: HTMLElement = el.querySelector("h3") ?? el;
                return clamp((documentY(h3) - startY) / range);
            };

            const fInsights = headingCenter(insights);
            const fCreative = headingCenter(creative);
            const fTechnology = headingCenter(technology);
            const fTechnologyTop = headingTop(technology);

            const TRAILING_SHARE = 0.4;
            const trailing = (anchor: number, prev: number) => {
                const dur = Math.max((anchor - prev) * TRAILING_SHARE, 0.05);
                return { start: anchor - dur, dur };
            };

            const r1 = { start: 0, dur: Math.max(fInsights, 0.05) };
            const r2 = trailing(fCreative, fInsights);
            const r3 = trailing(fTechnology, fCreative);
            const fadeStart = Math.max(fTechnology, fTechnologyTop);
            const fadeDur = Math.max(1 - fadeStart, 0.05);

            tl = gsap.timeline({ defaults: { svgOrigin: "0 0" } });

            tl.to(
                [blue, teal, red],
                { rotate: 120, duration: r1.dur },
                r1.start,
            ).to([blue, teal], { alpha: 0.2, duration: r1.dur }, r1.start);

            tl.to(
                [blue, teal, red],
                { rotate: 240, duration: r2.dur },
                r2.start,
            )
                .to([red], { alpha: 0.2, duration: r2.dur }, r2.start)
                .to([teal], { alpha: 0.7, duration: r2.dur }, r2.start);

            tl.to(
                [blue, teal, red],
                { rotate: 360, duration: r3.dur },
                r3.start,
            )
                .to([teal], { alpha: 0.2, duration: r3.dur }, r3.start)
                .to([blue], { alpha: 0.7, duration: r3.dur }, r3.start);

            tl.to(
                [blue, teal, red],
                { alpha: 0, cx: 0, cy: 0, scale: 0, duration: fadeDur },
                fadeStart,
            ).to([container], { alpha: 0, duration: fadeDur }, fadeStart);

            st = ScrollTrigger.create({
                animation: tl,
                trigger: hero,
                start: "top top",
                endTrigger: body,
                end: "center center",
                scrub: 0.3,
            });
        };

        build();

        let resizeTimeout: ReturnType<typeof setTimeout> | null = null;
        const onResize = () => {
            if (resizeTimeout) clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(build, 150);
        };
        window.addEventListener("resize", onResize);

        return () => {
            window.removeEventListener("resize", onResize);
            if (resizeTimeout) clearTimeout(resizeTimeout);
            tl?.kill();
            st?.kill();
        };
    }, []);

    return (
        <div
            className={concatClassNames([
                props.color_theme ? themes[props.color_theme] : undefined,
                styles.root,
            ])}
        >
            <div className={styles.fixedGraphic} ref={graphicRef}>
                <svg viewBox="-100 -100 200 200">
                    <circle
                        ref={blueRef}
                        cx={CIRCLE_POSITIONS.blue.cx}
                        cy={CIRCLE_POSITIONS.blue.cy}
                        r={55}
                        fill={colors.blue}
                        className={styles.colorCircle}
                    />
                    <circle
                        ref={tealRef}
                        cx={CIRCLE_POSITIONS.teal.cx}
                        cy={CIRCLE_POSITIONS.teal.cy}
                        r={55}
                        fill={colors.teal}
                        className={styles.colorCircle}
                    />
                    <circle
                        ref={redRef}
                        cx={CIRCLE_POSITIONS.red.cx}
                        cy={CIRCLE_POSITIONS.red.cy}
                        r={55}
                        fill={colors.red}
                        className={styles.colorCircle}
                    />
                </svg>
            </div>
            <ScrollIndicator />
            <div className={styles.storyWrapper}>
                {props.hero && (
                    <section ref={heroRef} className={styles.storySection}>
                        <RichTextBlock
                            value={props.hero}
                            largeHeadings={true}
                            className={styles.heroRichText}
                        />
                    </section>
                )}
                {props.insights && (
                    <section
                        ref={insightsRef}
                        style={{ "--slide-color": "var(--red)" }}
                        className={styles.storySection}
                    >
                        <h3>{props.insights.label}</h3>
                        {props.insights.body && (
                            <RichTextBlock
                                className={styles.richText}
                                largeHeadings={true}
                                value={props.insights.body}
                            />
                        )}
                    </section>
                )}
                {props.creative && (
                    <section
                        ref={creativeRef}
                        style={{ "--slide-color": "var(--teal)" }}
                        className={styles.storySection}
                    >
                        <h3>{props.creative.label}</h3>
                        {props.creative.body && (
                            <RichTextBlock
                                className={styles.richText}
                                largeHeadings={true}
                                value={props.creative.body}
                            />
                        )}
                    </section>
                )}
                {props.technology && (
                    <section
                        ref={technologyRef}
                        style={{ "--slide-color": "var(--blue)" }}
                        className={styles.storySection}
                    >
                        <h3>{props.technology.label}</h3>
                        {props.technology.body && (
                            <RichTextBlock
                                className={styles.richText}
                                largeHeadings={true}
                                value={props.technology.body}
                            />
                        )}
                    </section>
                )}
                {props.body && (
                    <section ref={bodyRef} className={styles.body}>
                        <RichTextBlock value={props.body} />
                    </section>
                )}
                {props.media_slider?.length && (
                    <div className={styles.slider} ref={sliderRef}>
                        {props.media_slider.map((block) => (
                            <MediaChooserBlock
                                key={block.id}
                                value={block.value}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
